import classNames from 'classnames';
import React, { useMemo, useEffect } from 'react';
import { withHeaderState } from '@bingads-webui-campaign-react/overview-card';

import { useTopLocationsData } from './use-top-locations-data';
import { MetricSelector } from './metric-selector';
import { Footer } from './footer';
import { NoData } from './no-data';
import { TopLocationsTable } from './top-locations-table';
import { TopLocationsMap } from './top-locations-map';
import { usePaginationConfig } from './use-pagination-config';
import { withPMaxPreferencesMetric } from './with-preferences-metric';
import { withBingMapsContext } from './with-bing-maps-context';
import { useCardStyles } from '../styles/card-styles';
import { VIEWS } from '../utils/const';
import { TopLocationsCardProps } from '../utils/types';


export function TopLocationsCardInternal({
  headerState,
  i18n,
  dateRange,
  metric,
  onMetricChange,
  perfMarkerUtils,
  campaignType,
}: TopLocationsCardProps) {
  const styles = useCardStyles();
  const {
    willFetchData,
    willRender,
    done,
  } = perfMarkerUtils;
  const { isLoading, locations } = useTopLocationsData({
    metric,
    dateRange,
    i18n,
    willFetchData,
    willRender,
  });
  const paginationConfig = usePaginationConfig(locations);
  const hasLocations = locations?.length > 0;

  useEffect(() => {
    if (!isLoading) {
      done();
    }
  }, [done, isLoading, willRender]);

  const contentArea = useMemo(() => {
    if (!hasLocations && !isLoading) {
      return <NoData i18n={i18n} />;
    }
    if (headerState?.isWideMode) {
      return (
        <div className={styles.wideContainer}>
          <div className={styles.wideContentWrapper}>
            <TopLocationsMap isLoading={isLoading} locations={locations} />
          </div>
          <div className={styles.wideContentWrapper}>
            <TopLocationsTable isLoading={isLoading} i18n={i18n} locations={locations} paginationConfig={paginationConfig} />
          </div>
        </div>
      );
    }

    if (headerState?.viewOption === VIEWS.MAP) {
      return <TopLocationsMap isLoading={isLoading} locations={locations} />;
    }
    return <TopLocationsTable isLoading={isLoading} i18n={i18n} locations={locations} paginationConfig={paginationConfig} />;
  }, [hasLocations, isLoading, headerState, i18n, locations, paginationConfig, styles]);


  const showPagination = hasLocations && !isLoading && (headerState?.isWideMode || headerState?.viewOption === VIEWS.TABLE);

  return (
    <div className={classNames('ba-top-locations-card', styles.container)}>
      <MetricSelector metric={metric} onMetricChange={onMetricChange} i18n={i18n} />
      {contentArea}
      <Footer
        showPagination={showPagination}
        paginationConfig={paginationConfig}
        i18n={i18n}
        campaignType={campaignType}
      />
    </div >
  );
}

export const TopLocationsCard = withPMaxPreferencesMetric(withBingMapsContext(withHeaderState(TopLocationsCardInternal)));
